import React, { useEffect, useState } from "react";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import {
  createGeneralSettings,
  updateGeneralSettings,
} from "../../api/SettingsApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { IGeneralSettings } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { Upload } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import {
  renderExistingFilePreview,
  renderFilePreview,
} from "../../exports/functions";

interface GeneralSettingProps {
  generalSettings: IGeneralSettings | null;
  setGeneralSettings: React.Dispatch<
    React.SetStateAction<IGeneralSettings | null>
  >;
}

const GeneralSetting = ({
  generalSettings,
  setGeneralSettings,
}: GeneralSettingProps) => {
  const { setLoading } = useLoader();
  const { canEdit } = usePermission();
  const [generalData, setGeneralData] = useState<
    IGeneralSettings & { file: File | null }
  >({
    websiteName: "",
    file: null,
    fileName: "",
    address: "",
    phone: "",
    email: "",
    timeZone: "",
    dateFormat: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    const updated = {
      ...generalData,
      [name]: value,
    } as typeof generalData;

    setGeneralData(updated);
    // setGeneralSettings((prev) => ({
    //   ...(prev ?? {}),
    //   [name]: value,
    // } as IGeneralSettings));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const updated = {
        ...generalData,
        fileName: file.name,
        file: file,
      };

      setGeneralData(updated);
      // setGeneralSettings((prev) => ({
      //   ...(prev ?? {}),
      //   fileName: file.name,
      // } as IGeneralSettings));
    }
  };

  const timeZoneOptions = [
    { value: "Asia/Kathmandu", label: "Asia/Kathmandu" },
    {
      value: "UTC (Coordinated Universal Time)",
      label: "UTC (Coordinated Universal Time)",
    },
    { value: "GMT (Greenwich Mean Time)", label: "GMT (Greenwich Mean Time)" },
    {
      value: "Daylight Saving Time (DST)",
      label: "Daylight Saving Time (DST)",
    },
  ];

  const dateFormatOptions = [
    { value: "MM / DD / YYYY", label: "MM / DD / YYYY" },
    { value: "DD / MM / YYYY", label: "DD / MM / YYYY" },
    { value: "YYYY / MM / DD", label: "YYYY / MM / DD" },
  ];

  const handleSelectChange = (
    name: keyof any,
    selected: SelectOption | null,
  ) => {
    const updated = {
      ...generalData,
      [name]: selected?.value,
    } as typeof generalData;

    setGeneralData(updated);
    // setGeneralSettings((prev) => ({
    //   ...(prev ?? {}),
    //   [name]: selected?.value,
    // } as IGeneralSettings));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = new FormData();

      payload.append("websiteName", generalData.websiteName);
      payload.append("address", generalData.address);
      payload.append("phone", generalData.phone);
      payload.append("email", generalData.email);
      payload.append("timeZone", generalData.timeZone);
      payload.append("dateFormat", generalData.dateFormat);

      if (generalData.file) {
        payload.append("documentUrl", generalData.file);
      }

      const response = generalSettings?.id
        ? await updateGeneralSettings(generalSettings.id, payload)
        : await createGeneralSettings(payload);

      if (response.status === 200 || response.status === 201) {
        const savedData = response.data;
        setGeneralSettings(savedData);
        setGeneralData((prev) => ({
          ...prev,
          ...savedData,
          file: prev.file,
        }));
        dynamicSuccess(
          generalSettings
            ? "General settings updated successfully!"
            : "General settings saved successfully!",
        );
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (generalSettings) {
      setGeneralData((prev) => ({
        ...prev,
        ...generalSettings,
      }));
    }
  }, [generalSettings]);

  return (
    <>
      <div className={`dashboard-card`} style={{ borderTopLeftRadius: 0 }}>
        <div className="row g-3">
          <div className="col-12">
            <h5 className="mb-1">Website Identity</h5>
            <small>Brand the application for your municipality.</small>

            <div className="mt-2 row g-3">
              {/* Website Name */}
              <div className="col-md-6">
                <label className="form-label">Website Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Website Here"
                  name="websiteName"
                  value={generalData.websiteName}
                  onChange={handleChange}
                />
                <small className="setting-description">
                  Displayed in the sidebar and browser tab
                </small>
              </div>

              {/* Address */}
              <div className="col-md-6">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Address Here"
                  name="address"
                  value={generalData.address}
                  onChange={handleChange}
                />
              </div>

              {/* Logo Upload */}
              <div className="col-12">
                <label className="form-label">Municipality Logo</label>

                <div
                  className={`upload-box p-4 ${!generalData?.file && !generalData?.documentUrl && "text-center"}`}
                >
                  {generalData?.file ? (
                    renderFilePreview(
                      generalData.file,
                      generalData.fileName,
                      (e: any) => handleFileChange(e),
                    )
                  ) : generalData?.documentUrl ? (
                    renderExistingFilePreview(
                      generalData.documentUrl,
                      handleFileChange,
                    )
                  ) : (
                    <>
                      <Upload />

                      <p className="mb-1">
                        Choose a file or drag and drop it here.
                      </p>

                      <small className="d-block mb-3">
                        (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                      </small>

                      <label className="btn btn-badge text-main-color">
                        Browse file
                        <input
                          hidden
                          type="file"
                          onChange={(e) => {
                            handleFileChange(e);
                          }}
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Phone + Email */}
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter Phone Here"
                  name="phone"
                  value={generalData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter Email Here"
                  name="email"
                  value={generalData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Time Zone + Date Format */}
              <div className="col-md-6">
                <label className="form-label">Time Zone</label>
                <Select
                  styles={customSelectStyles}
                  options={timeZoneOptions}
                  placeholder="Select time zone"
                  value={
                    timeZoneOptions.find(
                      (option) => option.value === generalData.timeZone,
                    ) || null
                  }
                  onChange={(selected) =>
                    handleSelectChange("timeZone", selected)
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Date Format</label>
                <Select
                  styles={customSelectStyles}
                  options={dateFormatOptions}
                  placeholder="Select date format"
                  value={
                    dateFormatOptions.find(
                      (option) => option.value === generalData.dateFormat,
                    ) || null
                  }
                  onChange={(selected) =>
                    handleSelectChange("dateFormat", selected)
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* Submit */}
              <div className="col-12 d-flex justify-content-start mt-4">
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={handleSubmit}
                  disabled={!canEdit("Settings")}
                  style={{
                    pointerEvents: canEdit("Settings") ? "auto" : "none",
                    opacity: canEdit("Settings") ? 1 : 0.5,
                  }}
                >
                  Send Change
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralSetting;
